import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import type { MeetingConnection } from '../../../lib/meetings-channel';
import { Modal } from '../../Modal';
import { Badge, Button, Switch } from '../ui';
import styles from './MeetingsSettingsModal.module.css';

/**
 * The Meetings connection's settings: the two switches, Reconnect and Disconnect. The phones draw the same
 * controls in a bottom sheet.
 *
 * **Presentational.** The section owns what each control does, so the click BI it already reports and the
 * decisions about closing around Reconnect and Disconnect stay in one place.
 *
 * **The talk switch is the only one the talk release decides, and only for turning talk ON.** Outside the
 * release a switch that is off is disabled and wears a "Coming soon" chip; the harness refuses that write
 * too, so this draws the refusal rather than being the gate. A switch that is already on stays on and
 * usable, because the harness always accepts turning talk off: an account taken out of the release must
 * be able to make her quiet.
 */
export function MeetingsSettingsModal({
  connection,
  canTalkAvailable,
  switchesLocked,
  disconnecting,
  onClose,
  onReconnect,
  onDisconnect,
  onAutoJoinChange,
  onCanTalkChange,
}: {
  connection: MeetingConnection;
  canTalkAvailable: boolean;
  /** Both switches go dead while an update is in flight or the site is outside the auto-join allowlist. */
  switchesLocked: boolean;
  disconnecting: boolean;
  onClose: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
  onAutoJoinChange: (on: boolean) => void;
  onCanTalkChange: (on: boolean) => void;
}) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const talkComingSoon = !canTalkAvailable && !connection.canTalk;
  const close = () => {
    logModalActionBi({ page_name: 'settings-connectors', modal_name: 'meetings_settings', action: 'close' });
    onClose();
  };

  return (
    <Modal
      open
      onClose={close}
      size="minimum"
      title={t('connectors.meetings.settings')}
      labelledBy="meetings-settings-title"
      headerSeparator
      footerSeparator
      maxHeightPx={650}
      scrollBody
      footer={
        <Button variant="outlined" onClick={close}>
          {t('common.close')}
        </Button>
      }
    >
      <div className={styles.body}>
        <div className={styles.switches}>
          <Switch
            id="meetings-auto-join"
            checked={connection.joinPolicy === 'ALL_VIDEO_EVENTS'}
            disabled={switchesLocked}
            label={t('connectors.meetings.autoJoin')}
            description={t('connectors.meetings.autoJoin.description')}
            onChange={onAutoJoinChange}
          />
          <Switch
            id="meetings-can-talk"
            checked={connection.canTalk}
            disabled={switchesLocked || talkComingSoon}
            label={
              talkComingSoon ? (
                <span className={styles.talkTitle}>
                  <span>{t('connectors.meetings.canTalk')}</span>
                  <Badge variant="coming-soon" size="tiny" label={t('connectors.state.comingSoon')} className={styles.soon} />
                </span>
              ) : (
                t('connectors.meetings.canTalk')
              )
            }
            description={t('connectors.meetings.canTalk.description')}
            onChange={onCanTalkChange}
          />
        </div>
        {/* Disconnect is always live: leaving is always the user's to do. */}
        <div className={styles.actions}>
          <Button variant="outlined" size="small" onClick={onReconnect}>
            {t('connectors.meetings.reconnect')}
          </Button>
          <Button variant="text" color="destructive" size="small" disabled={disconnecting} onClick={onDisconnect}>
            {t('connectors.meetings.disconnect')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
